import DocumentViewerModal from "components/DocumentViewer";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { useEffect, useState } from "react";
import {
  editAccusedSummon,
  editAppealSummon,
  editSummon,
} from "services/advocateComplaintService";
import { AdvocateComplaints, Litigation, Summon } from "types";
import { COMPLIANT_STATUS } from "utils/constantValues";
import { DateView } from "utils/helper";
import { ADVOCATE_ROLES } from "utils/roles";
import AddAppealSummonDecisionModal from "./AddAppealSummonDecisionModal";
import SummonConfirmationModal from "./SummonConfirmationModal";
import { useTranslation } from "contexts/i18nContext";

interface SummonProps {
  summonData: Summon;
  compliantData: AdvocateComplaints;
  appealLitigationData?: Litigation[];
  onSummonRefresh: any;
}

export default function AppealSummonCard({
  summonData,
  compliantData,
  appealLitigationData,
  onSummonRefresh,
}: SummonProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const canCoordinatorConfirmSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
  ]);
  const canUpdateHearingDate = useHasRole([
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canViewSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canViewConfirmedSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);

  // const { user } = useAuth();
  // const { showToast } = useToast();

  // const [loading, setLoading] = useState(false);
  // const [submitLoading, setSubmitLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [coordinatorConfirmationModal, setCoordinatorConfirmationModal] =
    useState<boolean>(false);
  const [isEditingHearingDate, setIsEditingHearingDate] = useState(false);
  const [hearingDateDraft, setHearingDateDraft] = useState("");
  const [isUpdatingHearingDate, setIsUpdatingHearingDate] = useState(false);

  const [canReassignProsecutor, setCanReassignProsecutor] = useState(false);

  useEffect(() => {
    if (appealLitigationData) {
      setCanReassignProsecutor(
        appealLitigationData?.find((a) =>
          a?.verdict_documents?.some(
            (v: { document_type: string }) =>
              v?.document_type === "council_decision_by_pro",
          ),
        )
          ? false
          : true,
      );
    }
  }, [appealLitigationData]);

  const toDateInputValue = (value?: string) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const startHearingDateEdit = () => {
    setHearingDateDraft(toDateInputValue(summonData?.hearing_date));
    setIsEditingHearingDate(true);
  };

  const cancelHearingDateEdit = () => {
    setIsEditingHearingDate(false);
    setHearingDateDraft("");
  };

  const handleUpdateHearingDate = async () => {
    if (!summonData?.id) {
      showToast("Summon not found", "error");
      return;
    }

    const currentDate = toDateInputValue(summonData?.hearing_date);
    if (!hearingDateDraft || hearingDateDraft === currentDate) {
      showToast("No date change detected", "error");
      return;
    }

    setIsUpdatingHearingDate(true);
    try {
      await editAppealSummon(
        {
          hearing_date: hearingDateDraft,
        } as any,
        summonData.id,
        false,
      );

      await Promise.resolve(onSummonRefresh?.());
      cancelHearingDateEdit();
      showToast("Hearing date updated", "success");
    } catch (err) {
      showToast("Failed to update hearing date", "error");
    } finally {
      setIsUpdatingHearingDate(false);
    }
  };

  if (!summonData) return null;

  return (
    <div className="mb-8">
      {(canViewSummon ||
        (canViewConfirmedSummon && summonData?.coordinator_confirmed)) &&
        summonData && (
          <div
            className={
              compliantData?.status === COMPLIANT_STATUS.CLOSED
                ? "mx-auto bg-white border-red-700 border-8 rounded-xl  overflow-hidden mb-4"
                : "mx-auto bg-white border-black border-8 rounded-xl  overflow-hidden mb-4"
            }
          >
            <div className="p-5">
              <h1 className="text-1xl font-bold text-gray-800 mb-3">
                {t("summon")}
              </h1>

              {summonData?.assiegned_prosecutor && (
                <div className="relative border border-gray-400 bg-white rounded-md p-2 my-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img
                        src={require("../../../images/avatar.png")}
                        alt="avatar"
                        className="w-8 h-8 rounded-full"
                      />
                      <div>
                        <p className="font-medium text-sm">
                          {summonData?.assiegned_prosecutor || "-"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t("assignedTo")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="">
                <hr />

                <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                  <p className="block text-gray-600">{t("hearingDate")}</p>
                  <div className="flex items-center gap-3">
                    <p className="block text-black text-sm font-medium">
                      {summonData?.hearing_date
                        ? DateView(summonData.hearing_date)
                        : "-"}
                    </p>
                    {canUpdateHearingDate && (
                      <button
                        type="button"
                        onClick={startHearingDateEdit}
                        className="text-xs font-medium text-red-700 hover:text-red-800 underline"
                      >
                        {t("updateDate")}
                      </button>
                    )}
                  </div>
                </div>

                {isEditingHearingDate && (
                  <div className="my-3 rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t("hearingDate")}
                      </label>
                      <input
                        type="date"
                        value={hearingDateDraft}
                        onChange={(e) => setHearingDateDraft(e.target.value)}
                        className="w-full md:w-64 rounded border border-gray-300 px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={isUpdatingHearingDate}
                        onClick={handleUpdateHearingDate}
                        className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                      >
                        {isUpdatingHearingDate ? "Updating..." : "Save Date"}
                      </button>
                      <button
                        type="button"
                        disabled={isUpdatingHearingDate}
                        onClick={cancelHearingDateEdit}
                        className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                      >
                        {t("cancel")}
                      </button>
                    </div>
                  </div>
                )}

                <hr />

                <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                  <p className="block text-gray-600">
                    {t("addressInformation")}
                  </p>
                  <p className="block text-black text-sm font-medium">
                    {`R-${summonData?.region || ""},  Z-${
                      summonData?.zone || ""
                    },  W-${summonData?.woreda || ""},  K-${
                      summonData?.kebele || ""
                    }`}
                  </p>
                </div>

                <hr />
              </div>

              {/* Summary Desc */}
              <div className="my-4">
                <h1 className="text-1xl font-bold text-gray-800 mb-1">
                  {t("summary")}
                </h1>
                <p className="text-gray-600 leading-relaxed">
                  {summonData?.summary || "-"}
                </p>
              </div>

              <hr />

              {/* Files */}
              <DocumentViewerModal
                documents={summonData?.summon_documents || []}
                baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                layout="list"
                label={t("accusedResponse")}
                hideEmptyFile={true}
              />
              {/* {summonData?.summon_file_details && (
                <div className="flex justify-between items-center p-2 bg-gray-50 rounded-md my-3">
                  <div className="flex items-center gap-2">
                    <div className="bg-[#5e3b1f] text-white font-bold text-xs px-2 py-1 rounded">
                      PDF
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {summonData?.summon_file_details?.file_name ||
                          "Summon Doc"}
                      </p>
                    </div>
                  </div>

                  <FileActions
                    fileUrl={summonData?.summon_file_details?.url || ""}
                  />
                </div>
              )} */}
            </div>
          </div>
        )}

      {/* {canCreateSummonDecision && (
        <div className="text-right">
          <button
            className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
            type="submit"
            onClick={() => setIsOpen(true)}
          >
            Add Decision
          </button>
        </div>
      )} */}

      {canCoordinatorConfirmSummon && !summonData?.coordinator_confirmed && (
        // ||(canAccusedConfirmSummon && !summonData?.accused_confirmed)
        <div className="text-right">
          <button
            className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
            type="submit"
            onClick={() => setCoordinatorConfirmationModal(true)}
          >
            {t("confirm")}
          </button>
        </div>
      )}

      {canCoordinatorConfirmSummon &&
        summonData?.coordinator_confirmed &&
        summonData?.assiegned_prosecutor &&
        canReassignProsecutor && (
          // ||(canAccusedConfirmSummon && !summonData?.accused_confirmed)
          <div className="text-right">
            <button
              className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
              type="submit"
              onClick={() => setCoordinatorConfirmationModal(true)}
            >
              {t("reassignProsecutor")}
            </button>
          </div>
        )}

      {summonData && (
        <AddAppealSummonDecisionModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          summonData={summonData}
          onRefresh={onSummonRefresh}
        />
      )}

      {summonData && (
        <SummonConfirmationModal
          isOpen={coordinatorConfirmationModal}
          setIsOpen={setCoordinatorConfirmationModal}
          summonData={summonData}
          complaintData={compliantData}
          onRefresh={onSummonRefresh}
          onComplaintsRefresh={onSummonRefresh}
        />
      )}
    </div>
  );
}
